import { useCallback, useEffect, useRef, useState } from "react";
import { interpretEscapes } from "./interpretEscapes";
import { connectChatSocket, disconnectChatSocket } from "./socketClient";
import { getStoredGuestId, setStoredGuestId } from "./chatIds";
import { isChatSocketDebugEnabled, logChatSocket } from "./chatSocketDebug";
import {
  buildServyHistoryPayload,
  buildServyQuestionPayload,
  getServyAnswerText,
  getServyFollowUps,
  getServyMessageId,
  isServyAnswer,
  mergeServyHistory,
  parseServyAck,
  parseServyHistory,
  toServyConversation,
  unwrapIncomingMessages,
} from "./servyMessages";

export const SERVY_CONNECT_TIMEOUT_MS = 20000;
/** Without a history ack by then, show the first-time screen rather than wait. */
export const SERVY_HISTORY_TIMEOUT_MS = 8000;
/** Many gateways never ack; after this the question counts as sent. */
export const SERVY_ACK_FALLBACK_MS = 600;
/** A question still unanswered after this is shown as failed, never as answered. */
export const SERVY_ANSWER_TIMEOUT_MS = 60000;

const extractGuestId = (payload) => {
  if (payload == null) return null;
  if (typeof payload === "string" || typeof payload === "number") {
    return String(payload);
  }
  return (
    payload.guestId ??
    payload.guest_id ??
    payload.id ??
    payload.userId ??
    payload.user?.id ??
    payload.data?.guestId ??
    payload.data?.id ??
    payload.body?.guestId ??
    null
  );
};

const newId = (prefix) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/** Full payloads for ?chatDebug=1 — the socket debug log truncates nested objects. */
const debugLog = (event, payload) => {
  if (!isChatSocketDebugEnabled()) return;
  logChatSocket({ direction: "in", event: `servy:${event}`, payload });
  // eslint-disable-next-line no-console
  console.debug("[servy]", event, JSON.stringify(payload));
};

/**
 * Socket lifecycle and question flow for the Servy chat. It shares the guest
 * chat's socket client but keeps its own conversation, so Servy messages never
 * mix with the host conversation. Only one question is in flight at a time,
 * which also ties each answer to its question.
 *
 * Messages: { id, role: "guest" | "servy", text, status, followUps? } where a
 * guest question goes "pending" → "sent" (awaiting the answer) → "answered",
 * or "failed".
 */
export const useServyChat = ({
  hostId,
  chatUuid,
  unitName,
  getLangCode,
  enabled = true,
}) => {
  const [status, setStatus] = useState("idle");
  const [connectionError, setConnectionError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inFlight, setInFlight] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [connectAttempt, setConnectAttempt] = useState(0);

  const socketRef = useRef(null);
  const guestIdRef = useRef(null);
  const pendingRef = useRef(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const contextRef = useRef(null);
  contextRef.current = { hostId, chatUuid, unitName, getLangCode };

  const updateMessage = useCallback((id, patch) => {
    setMessages((list) =>
      list.map((message) =>
        message.id === id ? { ...message, ...patch } : message,
      ),
    );
  }, []);

  const clearPending = useCallback(() => {
    const pending = pendingRef.current;
    if (pending) {
      clearTimeout(pending.ackTimer);
      clearTimeout(pending.answerTimer);
    }
    pendingRef.current = null;
    setInFlight(false);
  }, []);

  const failPending = useCallback(
    (reason) => {
      const pending = pendingRef.current;
      if (!pending) return;
      updateMessage(pending.id, { status: "failed", error: reason });
      clearPending();
    },
    [clearPending, updateMessage],
  );

  useEffect(() => {
    if (!enabled || !hostId || !chatUuid) return undefined;

    let intentional = false;
    let connectTimer = null;
    let historyTimer = null;
    let historyRequested = false;
    const storedGuestId = getStoredGuestId(chatUuid);
    guestIdRef.current = storedGuestId;
    setStatus("connecting");
    setConnectionError(null);

    const socket = connectChatSocket({
      hostId,
      qrCode: chatUuid,
      unitName,
      guestId: storedGuestId,
    });
    socketRef.current = socket;

    const fail = (reason) => {
      clearTimeout(connectTimer);
      failPending(reason);
      setStatus("error");
      setConnectionError(reason);
    };

    // Servy's earlier questions and answers, once per connection. A reconnect
    // loads them again, so they're merged into what's shown, never added to it.
    // Anything asked while it loads stays after the history.
    const loadHistory = () => {
      if (historyRequested) return;
      historyRequested = true;
      setHistoryLoading(true);
      historyTimer = setTimeout(
        () => setHistoryLoading(false),
        SERVY_HISTORY_TIMEOUT_MS,
      );

      socket.emit(
        "client:loadPrivateMessages",
        buildServyHistoryPayload({ qrCode: chatUuid, unitName, hostId }),
        (response) => {
          debugLog("ack:client:loadPrivateMessages", response);
          clearTimeout(historyTimer);
          setHistoryLoading(false);
          const { ok, messages: history } = parseServyHistory(response);
          if (!ok || history.length === 0) return;
          const earlier = toServyConversation(history, guestIdRef.current);
          setMessages((current) => mergeServyHistory(current, earlier));
        },
      );
    };

    // Ready once connected with a guest identity — Servy doesn't wait on the
    // host's availability.
    const markReady = () => {
      if (!socket.connected || !guestIdRef.current) return;
      clearTimeout(connectTimer);
      setStatus("ready");
      setConnectionError(null);
      loadHistory();
    };

    connectTimer = setTimeout(() => {
      if (!socket.connected || !guestIdRef.current) fail("connection_timeout");
    }, SERVY_CONNECT_TIMEOUT_MS);

    const onConnect = () => markReady();

    const onDisconnect = () => {
      if (intentional) return;
      fail("connection_lost");
    };

    const onConnectError = (err) => fail(err?.message || "connection_error");

    const onConnectionError = (payload) =>
      fail(
        typeof payload === "string"
          ? payload
          : payload?.message || "connection_error",
      );

    const onUserCreated = (payload) => {
      const id = extractGuestId(payload);
      if (!id) return;
      guestIdRef.current = String(id);
      setStoredGuestId(chatUuid, id);
      socket.auth = {
        ...(typeof socket.auth === "object" && socket.auth ? socket.auth : {}),
        guestId: String(id),
      };
      markReady();
    };

    const onNewPrivateMessage = (payload) => {
      debugLog("newPrivateMessage", payload);
      const pending = pendingRef.current;
      if (!pending) return;

      const answer = unwrapIncomingMessages(payload).find(
        (message) => isServyAnswer(message) && getServyAnswerText(message),
      );
      if (!answer) return;

      const answerId = getServyMessageId(answer) || newId("servy-answer");
      setMessages((list) => {
        if (list.some((message) => message.id === answerId)) return list;
        return [
          ...list.map((message) =>
            message.id === pending.id
              ? { ...message, status: "answered" }
              : message,
          ),
          {
            id: answerId,
            role: "servy",
            text: getServyAnswerText(answer),
            followUps: getServyFollowUps(answer),
            status: "received",
          },
        ];
      });
      clearPending();
    };

    const onException = (payload) => {
      debugLog("exception", payload);
      const message =
        typeof payload === "string"
          ? payload
          : payload?.message || payload?.error || "exception";
      const lower = String(message).toLowerCase();
      // Validation noise that isn't a failed send
      if (
        lower.includes("istyping") ||
        lower.includes("tempid") ||
        lower.includes("already typing")
      ) {
        return;
      }
      failPending(String(message));
    };

    const handlers = {
      connect: onConnect,
      disconnect: onDisconnect,
      connect_error: onConnectError,
      connectionError: onConnectionError,
      userCreated: onUserCreated,
      newPrivateMessage: onNewPrivateMessage,
      exception: onException,
    };
    Object.entries(handlers).forEach(([event, handler]) =>
      socket.on(event, handler),
    );

    if (socket.connected) onConnect();

    return () => {
      intentional = true;
      clearTimeout(connectTimer);
      clearTimeout(historyTimer);
      const pending = pendingRef.current;
      if (pending) {
        clearTimeout(pending.ackTimer);
        clearTimeout(pending.answerTimer);
        pendingRef.current = null;
      }
      Object.entries(handlers).forEach(([event, handler]) =>
        socket.off(event, handler),
      );
      disconnectChatSocket();
      socketRef.current = null;
    };
  }, [
    enabled,
    hostId,
    chatUuid,
    unitName,
    connectAttempt,
    failPending,
    clearPending,
  ]);

  const sendQuestion = useCallback(
    (rawText) => {
      const text = interpretEscapes(String(rawText ?? "").trim());
      if (!text.trim() || pendingRef.current) return false;

      const socket = socketRef.current;
      if (!socket?.connected || !guestIdRef.current) return false;

      const context = contextRef.current;
      const id = newId("servy-question");
      const pending = { id, text, acked: false };
      pendingRef.current = pending;
      setInFlight(true);
      setMessages((list) => [
        ...list,
        { id, role: "guest", text, status: "pending" },
      ]);

      const payload = buildServyQuestionPayload({
        qrCode: context.chatUuid,
        unitName: context.unitName,
        hostId: context.hostId,
        text,
        langCode: context.getLangCode?.(),
      });

      const markSent = () => {
        if (pendingRef.current !== pending || pending.acked) return;
        pending.acked = true;
        updateMessage(id, { status: "sent" });
      };

      socket.emit("client:sendPrivateMessage", payload, (response) => {
        debugLog("ack:client:sendPrivateMessage", response);
        if (pendingRef.current !== pending) return;
        const ack = parseServyAck(response);
        if (!ack.ok) {
          failPending(ack.error || "send_failed");
          return;
        }
        markSent();
      });

      pending.ackTimer = setTimeout(markSent, SERVY_ACK_FALLBACK_MS);
      pending.answerTimer = setTimeout(() => {
        if (pendingRef.current === pending) failPending("no_answer");
      }, SERVY_ANSWER_TIMEOUT_MS);
      return true;
    },
    [failPending, updateMessage],
  );

  const retryQuestion = useCallback(
    (id) => {
      if (pendingRef.current) return false;
      const failed = messagesRef.current.find(
        (message) => message.id === id && message.status === "failed",
      );
      if (!failed) return false;
      setMessages((list) => list.filter((message) => message.id !== id));
      return sendQuestion(failed.text);
    },
    [sendQuestion],
  );

  const reconnect = useCallback(() => {
    setConnectAttempt((attempt) => attempt + 1);
  }, []);

  return {
    status,
    connectionError,
    messages,
    inFlight,
    historyLoading,
    sendQuestion,
    retryQuestion,
    reconnect,
  };
};

export default useServyChat;
